import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OEMS,
  looksLikeSerial,
  normalizeSerial,
  type EnrichmentClient,
  type LookupRequest,
  type ProductLookup,
} from './api';
import { SECONDS_PER_LOOKUP } from './bulk';
import { OEM_NAMES, oemName, productTitle, warrantyBadge, type Tone } from './format';
import {
  BORDER,
  Badge,
  CARD,
  FIELD,
  Icon,
  IconTile,
  MONO,
  MUTED,
  RAISED,
  SOFT,
  SrOnly,
  TEXT,
  TONE_TEXT,
  buttonClass,
  useElapsed,
  type IconName,
} from './ui';

/** The OEM select's "no hint" value. */
const AUTO = '';

export type LookupPhase = 'idle' | 'checking' | 'answered' | 'failed';

export interface LookupState {
  /** Normalized. */
  serial: string;
  phase: LookupPhase;
  request: LookupRequest | null;
  answer: ProductLookup | null;
  /** Why the call itself failed (failed). */
  error: string | null;
}

const IDLE: LookupState = { serial: '', phase: 'idle', request: null, answer: null, error: null };

/**
 * One serial asked of the OEM through POST /v1/products/lookup. `ask` resolves the answer (null
 * when the call failed or a newer ask replaced it); the state says where it is. Leaving the page
 * aborts it.
 */
export function useSerialLookup(client: EnrichmentClient | null) {
  const { t } = useTranslation();
  const [state, setState] = useState<LookupState>(IDLE);
  const requestRef = useRef<AbortController | null>(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const ask = useCallback(async (request: LookupRequest): Promise<ProductLookup | null> => {
    if (!client) return null;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const serial = normalizeSerial(request.serial_number);
    setState({ serial, phase: 'checking', request, answer: null, error: null });
    try {
      const answer = await client.lookup(request, controller.signal);
      if (controller.signal.aborted) return null;
      setState({ serial, phase: 'answered', request, answer, error: null });
      return answer;
    } catch (e) {
      if (!controller.signal.aborted) {
        setState({ serial, phase: 'failed', request, answer: null, error: (e as Error).message || t('Something went wrong.') });
      }
      return null;
    }
  }, [client, t]);

  const reset = useCallback(() => {
    requestRef.current?.abort();
    setState(IDLE);
  }, []);

  return { state, ask, reset };
}

/** The OEM did not know the serial, could not be told, or is not supported. */
const NOT_KNOWN = new Set(['not_found', 'oem_unknown', 'unsupported']);

/** The checking steps; the API does not stream, so they advance on the time spent. */
export const CHECK_STEP_SECONDS = { detect: 3, store: SECONDS_PER_LOOKUP + 2 };

/** The step under way after `elapsed` seconds: 0 detect the OEM, 1 read its site, 2 store. */
export function checkingStep(elapsed: number, oemKnown: boolean): 0 | 1 | 2 {
  if (!oemKnown && elapsed < CHECK_STEP_SECONDS.detect) return 0;
  if (elapsed < CHECK_STEP_SECONDS.store) return 1;
  return 2;
}

/** The checking bar's width (%): it fills over the usual time and never quite ends. */
export function checkingProgress(elapsed: number): number {
  return Math.min(95, Math.round(5 + (elapsed / SECONDS_PER_LOOKUP) * 85));
}

type View = 'idle' | 'checking' | 'found' | 'needs_model' | 'not_known' | 'blocked' | 'failed';

/** OEMs whose sites block the agent: their serials are enriched locally and imported. */
const BLOCKED = new Set(['dell']);

function viewOf(state: LookupState): View {
  if (state.phase === 'idle') return 'idle';
  if (state.phase === 'checking') return 'checking';
  const answer = state.answer;
  if (answer?.found) return 'found';
  const oems = [state.request?.oem, answer?.oem?.code, answer?.check?.detection?.oem];
  if (oems.some((code) => code && BLOCKED.has(code))) return 'blocked';
  if (state.phase === 'failed') return 'failed';
  if (answer?.status === 'needs_more_info') return 'needs_model';
  if (answer?.status && NOT_KNOWN.has(answer.status)) return 'not_known';
  return 'failed';
}

/** The OEM's name for a heading: from the answer, the hint sent, or what was detected. */
function askedOem(state: LookupState): string | null {
  return oemName(state.answer?.oem)
    || oemName(state.request?.oem)
    || oemName(state.answer?.check?.detection?.oem ?? null);
}

export interface LookupCardHandle {
  /** Fills in the serial, brings the card into view, focuses it and looks the serial up. */
  lookUp: (serial: string) => void;
}

const FIELD_LABEL = 'text-xs font-semibold text-[#434A59] dark:text-[#c9d1d9]';

/**
 * "Look up a serial" (like DCC's "Test a lookup"): a serial, an optional OEM hint and model number,
 * and "Ask the OEM again" (refresh), sent to POST /v1/products/lookup. Stored serials come back at
 * once; a new one asks the OEM's site, with the checking steps meanwhile. Then what came back: a
 * found product in one line with "Open details" (`onOpen`, the drawer); the OEM needing the model
 * number (the Model number field takes focus); the OEM not knowing the serial (ask again); Dell,
 * whose site blocks the agent (`onImport`, to import answers enriched locally); or a failed check
 * (try again). Every answer may have changed what is stored (`onStored`).
 */
export const LookupCard = forwardRef<LookupCardHandle, {
  readonly client: EnrichmentClient | null;
  readonly onOpen: (serial: string, answer: ProductLookup) => void;
  readonly onStored: () => void;
  readonly onImport: () => void;
}>(function LookupCard({ client, onOpen, onStored, onImport }, ref) {
  const { t } = useTranslation();
  const lookup = useSerialLookup(client);
  const { state } = lookup;
  const elapsed = useElapsed(state.phase === 'checking');
  const view = viewOf(state);
  const [serial, setSerial] = useState('');
  const [oem, setOem] = useState<string>(AUTO);
  const [model, setModel] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [invalid, setInvalid] = useState<string | null>(null);
  const cardRef = useRef<HTMLElement>(null);
  const serialRef = useRef<HTMLInputElement>(null);
  const modelRef = useRef<HTMLInputElement>(null);
  const ids = { serial: useId(), oem: useId(), model: useId(), refresh: useId(), help: useId(), why: useId() };

  const ask = async (request: LookupRequest) => {
    setInvalid(null);
    const answer = await lookup.ask(request);
    if (!answer) return;
    onStored();
    // The model number goes to the site that asked for it.
    const detected = answer.oem?.code ?? answer.check?.detection?.oem;
    if (answer.status === 'needs_more_info' && !request.oem && detected && (OEMS as readonly string[]).includes(detected)) setOem(detected);
  };

  const submit = (text = serial, extra: Partial<LookupRequest> = {}) => {
    const typed = text.trim();
    if (!looksLikeSerial(typed)) {
      setInvalid(typed
        ? t('A serial number is 3–64 letters, digits, ‘.’, ‘_’ or ‘/’; spaces and hyphens are ignored.')
        : t('Type a serial number.'));
      serialRef.current?.focus();
      return;
    }
    void ask({
      serial_number: typed,
      ...(oem === AUTO ? {} : { oem }),
      ...(model.trim() ? { model_number: model.trim() } : {}),
      ...(refresh ? { refresh: true } : {}),
      ...extra,
    });
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useImperativeHandle(ref, () => ({
    lookUp: (next: string) => {
      setSerial(next);
      setModel('');
      cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      serialRef.current?.focus();
      // The model number typed for another serial does not go with this one.
      submitRef.current(next, { model_number: undefined });
    },
  }), []);

  // The OEM asks for the model number: that is the field to fill in next.
  useEffect(() => {
    if (view === 'needs_model') modelRef.current?.focus();
  }, [view, state.answer]);

  const who = askedOem(state) ?? t('The OEM');
  const shown = <span className={MONO}>{state.serial}</span>;
  const heading = (children: ReactNode) => <h3 className={`m-0 mb-1 text-[15px] font-bold ${TEXT}`}>{children}</h3>;
  let result: ReactNode = null;
  let announce = '';
  if (view === 'checking') {
    const name = oemName(state.request?.oem);
    announce = t('Checking {{serial}} with the OEM', { serial: state.serial });
    result = (
      <div className="max-w-[760px]">
        {heading(<>{t('Checking')} {shown} {name ? t('with {{oem}}', { oem: name }) : t('with the OEM')}</>)}
        <p className={`m-0 mb-[18px] text-sm ${MUTED}`}>
          {t('A stored serial comes back at once; a new one takes about 20 seconds. You can keep working; the result shows here.')}
        </p>
        <CheckingSteps oem={name} elapsed={elapsed} />
      </div>
    );
  } else if (view === 'found' && state.answer) {
    const answer = state.answer;
    announce = t('Found {{serial}}', { serial: answer.serial_number });
    result = <FoundLine answer={answer} onOpen={() => onOpen(answer.serial_number, answer)} />;
  } else if (view === 'needs_model') {
    const said = state.answer?.check?.error?.message;
    announce = t('{{oem}} needs the model number', { oem: who });
    result = (
      <Message icon="alert" tone="warn">
        {heading(<>{t('{{oem}} needs the model number for', { oem: who })} {shown}</>)}
        <p id={ids.why} className={`m-0 text-sm leading-normal ${MUTED}`}>
          {said ? `${said} ` : ''}
          {t('Some serials are shared across models. Add the model number from the unit’s label above and look it up again. Nothing was stored.')}
        </p>
      </Message>
    );
  } else if (view === 'not_known') {
    const status = state.answer?.status;
    announce = t('{{oem}} does not know {{serial}}', { oem: who, serial: state.serial });
    let why = t('Its warranty site has no record of this serial. Check it for typos, or pick the OEM and ask again.');
    if (status === 'oem_unknown') why = t('The OEM could not be told from this serial. Pick the OEM and ask again.');
    if (status === 'unsupported') why = t('This OEM’s warranty site is not supported yet.');
    result = (
      <Message icon="search" tone="neutral">
        {heading(<>{t('{{oem}} does not know the serial', { oem: who })} {shown}</>)}
        <p className={`m-0 mb-3 text-sm leading-normal ${MUTED}`}>{why} {t('No product was stored.')}</p>
        {/* A not-found answer is kept for 7 days: asking again asks the OEM itself. */}
        <button type="button" className={buttonClass({ small: true })} onClick={() => submit(state.request?.serial_number ?? serial, { refresh: true })}>
          {t('Ask again')}
        </button>
      </Message>
    );
  } else if (view === 'blocked') {
    announce = t('Dell’s site blocks the agent');
    result = (
      <Message icon="alert" tone="warn">
        {heading(t('Dell’s site blocks the agent'))}
        <p className={`m-0 mb-3 text-sm leading-normal ${MUTED}`}>
          {t('Dell serials are not looked up here. Send them to be enriched, then import the answers. Nothing was stored for')} {shown}.
        </p>
        <button type="button" className={buttonClass({ small: true })} onClick={onImport}>{t('Import answers')}</button>
      </Message>
    );
  } else if (view === 'failed') {
    const check = state.answer?.check;
    const reason = state.error || check?.error?.message || check?.error?.code || t('The OEM site did not answer.');
    announce = t('The check of {{serial}} failed', { serial: state.serial });
    result = (
      <Message icon="alert" tone="bad">
        {heading(<>{t('The check of')} {shown} {t('did not finish')}</>)}
        <p role="alert" className={`m-0 mb-3 text-sm leading-normal ${MUTED}`}>{reason} {t('Nothing was stored.')}</p>
        <button type="button" className={buttonClass({ small: true })} onClick={() => state.request && void ask(state.request)}>
          {t('Try again')}
        </button>
      </Message>
    );
  }

  return (
    <section ref={cardRef} aria-label={t('Look up a serial')} className={`${CARD} scroll-mt-4`}>
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-5 py-4 ${BORDER}`}>
        <h2 className={`m-0 flex-1 text-base font-bold ${TEXT}`}>{t('Look up a serial')}</h2>
        <span id={ids.help} className={`text-xs ${MUTED}`}>
          {t('Stored serials come back at once; a new serial asks the OEM’s site (about 20 seconds).')}
        </span>
      </div>
      <form
        noValidate
        className="grid grid-cols-1 items-end gap-3 px-5 py-4 md:grid-cols-[1.4fr_1fr_1fr_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.serial} className={FIELD_LABEL}>{t('Serial number')}</label>
          <input
            ref={serialRef}
            id={ids.serial}
            type="text"
            autoComplete="off"
            spellCheck={false}
            aria-describedby={ids.help}
            aria-invalid={invalid ? true : undefined}
            className={`${FIELD} ${MONO} w-full`}
            value={serial}
            onChange={(event) => {
              setSerial(event.target.value);
              // A model number belongs to the serial it was asked for.
              setModel('');
              setInvalid(null);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.oem} className={FIELD_LABEL}>{t('OEM')}</label>
          <select id={ids.oem} className={`${FIELD} w-full`} value={oem} onChange={(event) => setOem(event.target.value)}>
            <option value={AUTO}>{t('Detect automatically')}</option>
            {OEMS.map((code) => <option key={code} value={code}>{OEM_NAMES[code]}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.model} className={FIELD_LABEL}>{t('Model number')}</label>
          <input
            ref={modelRef}
            id={ids.model}
            type="text"
            autoComplete="off"
            placeholder={t('only if asked')}
            aria-describedby={view === 'needs_model' ? ids.why : undefined}
            className={`${FIELD} w-full`}
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.refresh} className={`flex h-[18px] items-center gap-2 text-[13px] ${SOFT}`}>
            <input
              id={ids.refresh}
              type="checkbox"
              className="size-4 accent-[#0B1B3D] dark:accent-[#e6edf3]"
              checked={refresh}
              onChange={(event) => setRefresh(event.target.checked)}
            />
            {t('Ask the OEM again')}
          </label>
          <button type="submit" className={buttonClass({ primary: true })} disabled={!client || state.phase === 'checking'}>
            {t('Look up')}
          </button>
        </div>
      </form>
      {oem === 'dell' && view !== 'blocked' && (
        <p className={`m-0 -mt-1 px-5 pb-4 text-xs ${TONE_TEXT.warn}`}>
          {t('Dell’s site blocks the agent: only Dell serials already imported come back. Enrich others locally, then import the answers.')}
        </p>
      )}
      {invalid && <p role="alert" className={`m-0 -mt-1 px-5 pb-4 text-[13px] ${TONE_TEXT.bad}`}>{invalid}</p>}
      <SrOnly role="status">{announce}</SrOnly>
      {result && <div className={`border-t px-5 py-5 ${BORDER}`}>{result}</div>}
    </section>
  );
});

/** A found answer in one line: product, warranty, where it came from, and "Open details". */
function FoundLine({ answer, onOpen }: { readonly answer: ProductLookup; readonly onOpen: () => void }) {
  const { t } = useTranslation();
  const warranty = answer.warranty;
  const badge = warrantyBadge(warranty?.status);
  const name = oemName(answer.oem);
  let source = { tone: 'neutral' as Tone, text: t('Served from stored data') };
  if (answer.served_from === 'agent') source = { tone: 'info', text: name ? t('Asked {{oem}} just now', { oem: name }) : t('Asked the OEM just now') };
  if (answer.stale) source = { tone: 'warn', text: t('Stored data: {{oem}} could not be asked now', { oem: name ?? t('the OEM') }) };
  let until: string | null = null;
  if (warranty?.status === 'active' && warranty.end_date) until = t('until {{date}}', { date: warranty.end_date });
  else if (warranty?.status === 'expired' && warranty.end_date) until = t('ended {{date}}', { date: warranty.end_date });
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[10px] border px-4 py-3.5 ${BORDER} ${RAISED}`}>
      <div className="min-w-[240px] flex-[1_1_360px]">
        <div className={`text-[15px] font-bold ${TEXT}`}>{productTitle(answer.product) ?? answer.serial_number}</div>
        <div className={`${MONO} mt-0.5 text-xs ${MUTED}`}>
          {[answer.serial_number, answer.product?.product_number, name].filter(Boolean).join(' · ')}
        </div>
      </div>
      <span className="inline-flex items-center gap-2">
        <Badge tone={badge.tone}>{t(badge.label)}</Badge>
        {until && <span className={`text-xs ${MUTED}`}>{until}</span>}
      </span>
      <Badge tone={source.tone}>{source.text}</Badge>
      <button type="button" className={buttonClass({ small: true })} onClick={onOpen}>{t('Open details')}</button>
    </div>
  );
}

function Message({ icon, tone, children }: {
  readonly icon: IconName;
  readonly tone: Tone;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex max-w-[760px] items-start gap-4">
      <IconTile name={icon} tone={tone} size={40} iconSize={20} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** The checking bar and its three steps; the step under way shows the seconds spent. */
export function CheckingSteps({ oem, elapsed }: { readonly oem: string | null; readonly elapsed: number }) {
  const { t } = useTranslation();
  const step = checkingStep(elapsed, Boolean(oem));
  const steps = [
    step > 0 ? (oem ? t('OEM: {{oem}}', { oem }) : t('OEM detected')) : t('Detecting the OEM'),
    oem ? t('Reading {{oem}}’s warranty site', { oem }) : t('Reading the OEM’s warranty site'),
    t('Storing the answer for your tenant'),
  ];
  return (
    <>
      <div className="mb-[18px] h-1.5 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]">
        <div className="h-1.5 rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3] transition-[width] duration-1000" style={{ width: `${checkingProgress(elapsed)}%` }} />
      </div>
      <ol aria-label={t('Steps')} className="m-0 flex list-none flex-col gap-3 p-0">
        {steps.map((label, index) => {
          const done = index < step;
          const current = index === step;
          return (
            <li key={index} aria-current={current ? 'step' : undefined} className={`flex items-center gap-2.5 text-[13px] ${!done && !current ? 'text-[#5C6B85] dark:text-[#8b949e]' : 'text-[#0B1B3D] dark:text-[#e6edf3]'}`}>
              {done && (
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[#D1FAE5] dark:bg-[#10B981]/15">
                  <Icon name="check" size={12} className={TONE_TEXT.ok} strokeWidth={3} />
                </span>
              )}
              {current && (
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3]">
                  <span className="size-1.5 rounded-full bg-white dark:bg-[#0d1117]" />
                </span>
              )}
              {!done && !current && <span className="box-border inline-flex size-5 shrink-0 rounded-full border-2 border-solid border-[#CBD5E1] dark:border-[#454c56]" />}
              {current ? <strong>{label}</strong> : label}
              {current && <span className="text-[#5C6B85] dark:text-[#8b949e]">{t('{{seconds}} s', { seconds: elapsed })}</span>}
            </li>
          );
        })}
      </ol>
    </>
  );
}
